import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import axios from 'axios';
import { pageContext } from 'ctm-util-functions';
import { getBrand } from 'ctm-app/client/store/selectors';

const Container = styled.div`
    font-weight: bold;
    margin-top: 20vh;
    width: 100%;
    font-family: var(--family-base);
    position: absolute;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    text-align: center;
`;

const Text = styled.h2`
    margin-bottom: 60px;
    font-size: 28px;
    font-weight: 600;
    line-height: 136%;
`;

const Spinner = styled.div`
    margin: 0 auto;
    width: 85px;
    height: 85px;
    border-radius: 50%;
    border: 8.5px solid #0b60b7;
    border-bottom-color: transparent;
    animation: spin 1s linear infinite;
    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};

/**
 * Asynchronously fetches data using axios and performs necessary updates based on the retrieved data.
 *
 * @return {Promise<void>} A Promise that resolves once the data fetching and updates are complete.
 */

const _PrefillCheck = ({ vertical, journeyUrl, accountUrl, updateTokens, serverConfigEnv, trackPrefillCheckSuccess, trackPrefillCheckFailed, brandCode }) => {
    const anonId = sessionStorage.getItem('user_anonymous_id');
    const userId = sessionStorage.getItem('user_id');
    const expId = sessionStorage.getItem('experience-id');
    const accessToken = localStorage.getItem('access_token');
    const queryString = window?.location?.search;

    // eslint-disable-next-line consistent-return
    const handleTokens = async () => {
        let isExpired = false;
        if (accessToken !== null && accessToken !== undefined) {
            const decodedJwt = parseJwt(accessToken);
            if (decodedJwt !== null) {
                isExpired = decodedJwt.exp * 1000 < Date.now();
            }
        }
        if (accessToken === null || accessToken === undefined || (accessToken && isExpired)) {
            try {
                const { data } = await axios.get(`${accountUrl}/token/anonymous/${brandCode}`);
                updateTokens(data.access_token, data.refresh_token);
                localStorage.setItem('access_token', data.access_token);
                return data.access_token;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('error', error);
            }
        }
    };

    const fetchData = async () => {
        try {
            const token = await handleTokens();
            const { data: journeyData } = await axios.get(`${journeyUrl}${serverConfigEnv === 'local' ? '' : '/'}journey/prefill/check`, {
                headers: { Authorization: `Bearer ${accessToken || token}` },
                params: {
                    brand_code: brandCode,
                    anonymous_id: anonId,
                    user_id: userId,
                    experiment_id: expId
                }
            });

            // prevent choo from prefilling regardless of the response that is received
            // this be because choo does not have the json schema setup for /prefill_verify
            // and will crash if it tries to prefill
            const brandsInelligibleForPrefill = ['choo'];

            if (!brandsInelligibleForPrefill.includes(brandCode) && journeyData.hasPrefillCandidate) {
                pageContext.getProps().browserHistory.push({
                    pathname: `/${vertical}/journey/prefill_verify`,
                    search: queryString
                });
                trackPrefillCheckSuccess();
            } else {
                pageContext.getProps().browserHistory.push({
                    pathname: `/${vertical}/journey/start`,
                    search: queryString
                });
                trackPrefillCheckFailed();
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('error', error);
            pageContext.getProps().browserHistory.push({
                pathname: `/${vertical}/journey/start`,
                search: queryString
            });
            trackPrefillCheckFailed();
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const pageState = pageContext.getProps().pageState;

    const pagesToExclude = ['/car-insurance/journey/results', '/home-contents-insurance/journey/results', '/car-insurance/journey/verify'];

    return (
        <Container>
            {!pagesToExclude.includes(pageState) && (
                <>
                    <Text>Just a moment...</Text>
                    <Spinner />
                </>
            )}
        </Container>
    );
};

const mapStateToProps = (state) => {
    const vertical = state.getIn(['page', 'vertical']);
    const journeyUrl = state.getIn(['serverConfig', 'journeyServiceBaseUrl']);
    const accountUrl = state.getIn(['serverConfig', 'accountServiceBaseUrl']);
    const env = state.getIn(['serverConfig', 'env']);
    const serverConfigEnv = state.getIn(['serverConfig', 'env']);
    const brandCode = getBrand(state);

    return { vertical, journeyUrl, env, accountUrl, serverConfigEnv, brandCode };
};

const mapDispatchToProps = (dispatch) => ({
    updateTokens: (accessToken, refreshToken) => {
        dispatch({ type: 'SET_AUTH_TOKENS', access_token: accessToken, refresh_token: refreshToken });
    },
    trackPrefillCheckSuccess: () => {
        dispatch({ type: 'PREFILL_CHECK_SUCCESS' });
    },
    trackPrefillCheckFailed: () => {
        dispatch({ type: 'PREFILL_CHECK_FAILED' });
    }
});

_PrefillCheck.propTypes = {
    dispatch: PropTypes.func,
    updateTokens: PropTypes.func
};

const PrefillCheck = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PrefillCheck);

export { _PrefillCheck, PrefillCheck, parseJwt };
